import { getPrettyCarrierName } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { CreateLabelModal } from "../../../../return/return-modals/create-label/create-label-modal";
import {
  ConfirmationPageInfo,
  CreateLabelTarget,
} from "../../../../return/return-modals/create-label/create-label-modal-types";
import { ResendModal } from "../../../../return/return-modals/resend-modal";
import {
  applyLabelCorrections,
  CorrectedAddressInput,
} from "./apply-label-corrections";
import { useAskRedoReturnQuery } from "./use-ask-redo-return-query";

interface ManageReturnLabelInput {
  returnId?: string;
  mode?: "new" | "replace";
  shipmentId?: string | null;
  correctedFromAddress?: CorrectedAddressInput | null;
  correctedToAddress?: CorrectedAddressInput | null;
  correctedWeightLb?: number | null;
  reason?: string | null;
}

function readInput(input: unknown): ManageReturnLabelInput {
  return typeof input === "object" && input !== null ? input : {};
}

export interface ReturnLabelCardProps {
  input: unknown;
  toolCallId: string;
  disabled: boolean;
}

export const ReturnLabelCard = memo(function ReturnLabelCard({
  input,
  disabled,
}: ReturnLabelCardProps) {
  const args = readInput(input);
  const returnId = args.returnId;
  const { formatCurrency } = useContext(CurrencyContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [purchased, setPurchased] = useState<
    ConfirmationPageInfo | undefined
  >();
  const returnQuery = useAskRedoReturnQuery(returnId, "askRedoLabelReturn");

  const correctedReturn = useMemo(
    () =>
      returnQuery.data
        ? applyLabelCorrections(returnQuery.data, {
            shipmentId: args.shipmentId,
            correctedFromAddress: args.correctedFromAddress,
            correctedToAddress: args.correctedToAddress,
            correctedWeightLb: args.correctedWeightLb,
          })
        : undefined,
    [returnQuery.data, args],
  );

  const handleShippingCreated = (info: ConfirmationPageInfo) => {
    if (!info.success) {
      return;
    }
    setModalOpen(false);
    void returnQuery.refetch();
    setPurchased(info);
  };

  if (!returnId) {
    return null;
  }

  const target: CreateLabelTarget | undefined =
    args.mode === "replace"
      ? args.shipmentId
        ? { type: "replace", shipmentId: args.shipmentId }
        : undefined
      : { type: "add" };

  const trackingNumber = purchased?.success
    ? purchased.trackingNumber
    : undefined;

  const purchasedShipment = trackingNumber
    ? [
        ...(returnQuery.data?.shipments ?? []),
        ...(returnQuery.data?.shipment ? [returnQuery.data.shipment] : []),
      ].find((shipment) => shipment._shipment?.tracking_code === trackingNumber)
    : undefined;

  const carrier = purchasedShipment?._shipment?.selected_rate?.carrier;
  const carrierName = carrier
    ? getPrettyCarrierName(
        carrier,
        purchasedShipment?._shipment?.shipping_provider_type,
      )
    : undefined;
  const trackingUrl = purchasedShipment?._shipment?.tracker?.public_url;

  const labelUrl =
    purchased?.success && purchased.kind === "label"
      ? purchased.labelUrl ||
        purchasedShipment?.postage_label ||
        purchasedShipment?._shipment?.postage_label?.label_url ||
        undefined
      : undefined;
  const qrCodeUrl = purchased?.success ? purchased.qrCodeUrl : undefined;

  const isDropoff = purchased?.success && purchased.kind === "dropoff";
  const charge = purchased?.success ? purchased.charge : undefined;
  const summary = [
    carrierName,
    charge
      ? `${formatCurrency(charge)} added to your next invoice`
      : "No charge",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Flex dir="column" gap="sm" pb="md" w="full">
      {args.reason ? (
        <Text fontSize="sm" textColor="secondary">
          {args.reason}
        </Text>
      ) : null}
      {!target ? (
        <Text fontSize="sm" textColor="error">
          Couldn&apos;t open the label options for this return.
        </Text>
      ) : returnQuery.isError ? (
        <Text fontSize="sm" textColor="error">
          Couldn&apos;t load this return&apos;s label details.
        </Text>
      ) : purchased?.success ? (
        <Flex
          bgColor="primary"
          borderColor="secondary"
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="none"
        >
          <Flex align="flex-start" dir="row" gap="sm" p="lg">
            <RedoIcon color="success" Icon={CheckCircleIcon} size={18} />
            <Flex dir="column" gap="xxs" minW="none">
              <Text fontSize="sm" fontWeight="semibold">
                {isDropoff ? "Drop-off created" : "Label created"}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {summary}
              </Text>
              {trackingNumber ? (
                <Text fontSize="xs" textColor="secondary">
                  {`Tracking ${trackingNumber}`}
                </Text>
              ) : null}
            </Flex>
          </Flex>
          <Flex
            align="center"
            borderColor="secondary"
            borderStyle="solid"
            borderTopWidth="1px"
            dir="row"
            gap="sm"
            p="md"
            wrap="wrap"
          >
            {labelUrl ? (
              <RedoButton
                hierarchy="secondary"
                onClick={() => window.open(labelUrl, "_blank")}
                size="sm"
                text="Label"
              />
            ) : null}
            {qrCodeUrl ? (
              <RedoButton
                hierarchy="secondary"
                onClick={() => window.open(qrCodeUrl, "_blank")}
                size="sm"
                text="QR code"
              />
            ) : null}
            {trackingUrl ? (
              <RedoButton
                hierarchy="secondary"
                onClick={() => window.open(trackingUrl, "_blank")}
                size="sm"
                text="Track"
              />
            ) : null}
            <RedoButton
              hierarchy="secondary"
              onClick={() => setResendOpen(true)}
              size="sm"
              text="Email to customer"
            />
          </Flex>
        </Flex>
      ) : (
        <RedoButton
          disabled={disabled || returnQuery.isLoading || !correctedReturn}
          hierarchy="primary"
          onClick={() => setModalOpen(true)}
          pending={returnQuery.isLoading}
          size="md"
          text={
            target?.type === "replace"
              ? "Review & replace label"
              : "Review & buy label"
          }
        />
      )}
      {modalOpen && correctedReturn && target ? (
        <CreateLabelModal
          onClose={() => setModalOpen(false)}
          onLabelCreated={handleShippingCreated}
          return_={correctedReturn}
          target={target}
        />
      ) : null}
      {returnQuery.data ? (
        <ResendModal
          isOpen={resendOpen}
          onClose={() => setResendOpen(false)}
          return_={returnQuery.data}
        />
      ) : null}
    </Flex>
  );
});
